import type { RouterOutputs } from "@onadssuper/api-client";
import { ROLE_LABELS, type WorkspaceRole } from "@onadssuper/validators";
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { MailPlus, ShieldCheck, UserMinus, Users } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";
import {
  Badge,
  Button,
  Card,
  CardHeader,
  Dialog,
  EmptyState,
  ErrorState,
  PageHeader,
  PageSpinner,
  RoleSelect,
  TBody,
  TD,
  TH,
  THead,
  TR,
  Table,
} from "../../../components/ui";
import { InviteDialog } from "../../../features/members/invite-dialog";
import { canManageMember, roleChangeOptions } from "../../../features/workspace/permissions";
import { useCan, useCurrentWorkspace } from "../../../features/workspace/workspace-context";
import { Avatar } from "../../../lib/avatar";
import { useTRPC } from "../../../lib/trpc";
import { formatDate } from "../../../lib/utils";

export const Route = createFileRoute("/w/$slug/members")({ component: Members });

type Member = RouterOutputs["members"]["list"]["items"][number];

function Members() {
  const canInvite = useCan("member.invite");
  const [inviting, setInviting] = useState(false);
  return (
    <>
      <PageHeader
        title="Members"
        description="Everyone in this workspace, agency and client."
        actions={
          canInvite && (
            <Button onClick={() => setInviting(true)}>
              <MailPlus className="size-4" /> Invite
            </Button>
          )
        }
      />
      <div className="flex flex-col gap-6">
        <MembersTable />
        <PendingInvitations />
      </div>
      {canInvite && <InviteDialog open={inviting} onOpenChange={setInviting} />}
    </>
  );
}

function MembersTable() {
  const trpc = useTRPC();
  const workspace = useCurrentWorkspace();
  const canChangeRole = useCan("member.change_role");
  const canRemove = useCan("member.remove");
  const [removing, setRemoving] = useState<Member | null>(null);
  const members = useInfiniteQuery(
    trpc.members.list.infiniteQueryOptions(
      { limit: 25 },
      { getNextPageParam: (last) => last.nextCursor ?? undefined },
    ),
  );

  if (members.isPending) return <PageSpinner />;
  if (members.isError)
    return <ErrorState error={members.error} onRetry={() => void members.refetch()} />;
  const rows = members.data.pages.flatMap((p) => p.items);

  return (
    <Card>
      <CardHeader
        title="People"
        description={`${rows.length}${members.hasNextPage ? "+" : ""} members`}
      />
      {rows.length === 0 ? (
        <EmptyState icon={<Users className="size-8" />} title="No members yet" />
      ) : (
        <Table>
          <THead>
            <TR>
              <TH>Name</TH>
              <TH>Role</TH>
              <TH>Two-factor</TH>
              <TH>Joined</TH>
              {canRemove && <TH className="text-right">Actions</TH>}
            </TR>
          </THead>
          <TBody>
            {rows.map((m) => {
              const manageable = canManageMember(workspace.role, m.role);
              const isSelf = m.membershipId === workspace.membershipId;
              return (
                <TR key={m.membershipId}>
                  <TD>
                    <div className="flex items-center gap-3">
                      <Avatar name={m.name} image={m.image} />
                      <div className="min-w-0">
                        <p className="truncate font-medium">
                          {m.name}{" "}
                          {isSelf && <span className="text-xs font-normal text-subtle">(you)</span>}
                        </p>
                        <p className="truncate text-xs text-muted">{m.email}</p>
                      </div>
                    </div>
                  </TD>
                  <TD className="min-w-48">
                    {canChangeRole && manageable ? (
                      <RoleChanger member={m} />
                    ) : (
                      <Badge tone={m.role.startsWith("client") ? "neutral" : "primary"}>
                        {ROLE_LABELS[m.role]}
                      </Badge>
                    )}
                  </TD>
                  <TD>
                    {m.mfaEnabled ? (
                      <Badge tone="success">
                        <ShieldCheck className="size-3" aria-hidden /> On
                      </Badge>
                    ) : (
                      <Badge>Off</Badge>
                    )}
                  </TD>
                  <TD className="whitespace-nowrap text-muted">{formatDate(m.joinedAt)}</TD>
                  {canRemove && (
                    <TD className="text-right">
                      {manageable && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setRemoving(m)}
                          aria-label={`Remove ${m.name}`}
                        >
                          <UserMinus className="size-4" />
                        </Button>
                      )}
                    </TD>
                  )}
                </TR>
              );
            })}
          </TBody>
        </Table>
      )}
      {members.hasNextPage && (
        <div className="flex justify-center border-t border-border p-3">
          <Button
            variant="secondary"
            size="sm"
            loading={members.isFetchingNextPage}
            onClick={() => void members.fetchNextPage()}
          >
            Load more
          </Button>
        </div>
      )}
      <RemoveDialog member={removing} onClose={() => setRemoving(null)} />
    </Card>
  );
}

function RoleChanger({ member }: { member: Member }) {
  const trpc = useTRPC();
  const queryClient = useQueryClient();
  const workspace = useCurrentWorkspace();
  const roles: WorkspaceRole[] = Array.from(
    new Set<WorkspaceRole>([member.role, ...roleChangeOptions(workspace.role, member.role)]),
  );
  const change = useMutation(
    trpc.members.changeRole.mutationOptions({
      onSuccess: async (result) => {
        toast.success(`${member.name} is now ${ROLE_LABELS[result.role]}`);
        await queryClient.invalidateQueries({ queryKey: trpc.members.list.pathKey() });
      },
      onError: () => void queryClient.invalidateQueries({ queryKey: trpc.members.list.pathKey() }),
    }),
  );
  return (
    <RoleSelect
      aria-label={`Role for ${member.name}`}
      roles={roles}
      value={member.role}
      disabled={change.isPending}
      onValueChange={(role) => {
        if (role !== member.role)
          change.mutate({
            membershipId: member.membershipId,
            role,
            expectedVersion: member.version,
          });
      }}
    />
  );
}

function RemoveDialog({ member, onClose }: { member: Member | null; onClose: () => void }) {
  const trpc = useTRPC();
  const queryClient = useQueryClient();
  const remove = useMutation(
    trpc.members.remove.mutationOptions({
      onSuccess: async () => {
        toast.success(`${member?.name ?? "Member"} was removed`);
        await queryClient.invalidateQueries({ queryKey: trpc.members.list.pathKey() });
        await queryClient.invalidateQueries({ queryKey: trpc.invitations.list.queryKey() });
        onClose();
      },
    }),
  );
  return (
    <Dialog
      open={member !== null}
      onOpenChange={(open) => !open && onClose()}
      title={`Remove ${member?.name ?? "member"}?`}
      description="They lose access to this workspace immediately, and invitations they sent stop working. You can invite them again later."
    >
      <div className="flex justify-end gap-2">
        <Button variant="secondary" onClick={onClose}>
          Cancel
        </Button>
        <Button
          variant="danger"
          loading={remove.isPending}
          onClick={() => member && remove.mutate({ membershipId: member.membershipId })}
        >
          Remove
        </Button>
      </div>
    </Dialog>
  );
}

function PendingInvitations() {
  const trpc = useTRPC();
  const queryClient = useQueryClient();
  const canInvite = useCan("member.invite");
  const invitations = useQuery(trpc.invitations.list.queryOptions());
  const revoke = useMutation(
    trpc.invitations.revoke.mutationOptions({
      onSuccess: async () => {
        toast.success("Invitation revoked");
        await queryClient.invalidateQueries({ queryKey: trpc.invitations.list.queryKey() });
      },
    }),
  );

  if (invitations.isPending) return null;
  if (invitations.isError)
    return <ErrorState error={invitations.error} onRetry={() => void invitations.refetch()} />;
  if (invitations.data.length === 0) return null;

  return (
    <Card>
      <CardHeader title="Pending invitations" />
      <Table>
        <THead>
          <TR>
            <TH>Email</TH>
            <TH>Role</TH>
            <TH>Expires</TH>
            {canInvite && <TH className="text-right">Actions</TH>}
          </TR>
        </THead>
        <TBody>
          {invitations.data.map((inv) => (
            <TR key={inv.id}>
              <TD className="font-medium">{inv.email}</TD>
              <TD>
                <Badge>{ROLE_LABELS[inv.role]}</Badge>
              </TD>
              <TD className="whitespace-nowrap">
                {inv.expired ? (
                  <Badge tone="warning">Expired</Badge>
                ) : (
                  <span className="text-muted">{formatDate(inv.expiresAt)}</span>
                )}
              </TD>
              {canInvite && (
                <TD className="text-right">
                  <Button
                    variant="ghost"
                    size="sm"
                    disabled={revoke.isPending && revoke.variables?.invitationId === inv.id}
                    onClick={() => revoke.mutate({ invitationId: inv.id })}
                  >
                    Revoke
                  </Button>
                </TD>
              )}
            </TR>
          ))}
        </TBody>
      </Table>
    </Card>
  );
}
